<template>
  <div>

    <Card class="card card-custom border-0 position-relative">
      <div class="position-absolute top-0 end-0 left-100">
        <Select2
            container-class="col-12"
            :multiple="false"
            :show-search="true"
            selectClass="form-control form-select form-select-solid rounded-0 min-w-150px shadow-xl bg-white border-2"
            :name="`selected-${selectedCompany?.id}-office`"
            :placeholderText="$t('society')"
            :is-required="false"
            color="primary"
            v-model="query.selectedOffice">
          <option v-for="office in offices" :key="office.id" :value="office.id">
            {{ office.name }}
          </option>
        </Select2>
      </div>
      <template #cardBody>
        <div class="row mt-10" >
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="card-label">{{ $t('recurrentExpense.list') }}</h3>
            <Button btn-class="btn-sm" type="new" btn-text="recurrentExpense.new.title"  v-if="$can(abilities.CREATE_EXPENSE_RECURRING)" @click.prevent="createNewRecurrent"/>
          </div>
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="recurrent-expense-datatable" style="margin-top: 13px !important; cursor:pointer">
            <thead>
            <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
              <th> {{$t("recurrentExpense.label.title")}}</th>
              <th> {{$t("recurrentExpense.label.expenseDate")}}</th>
              <th> {{$t("recurrentExpense.label.amount")}}</th>
              <th> {{$t("recurrentExpense.label.status")}}</th>
              <th>{{$t("actions")}}</th>
            </tr>
            </thead>
          </table>
        </div>
      </template>
    </Card>

  </div>
</template>

<script setup>
import {BASE_URL} from "@/services/api";
import { useRouter } from "vue-router"
import Ls from "@/services/ls";
import { useI18n } from 'vue-i18n'
import {useStore} from "vuex";
import {computed, nextTick, onMounted, reactive, ref, watch} from "vue"
import {useHasAbility} from "@/composables/hasAbility";
import { useLocales } from '@/composables/locales';
import Button from "@/components/Button.vue"
import {useSwal} from "@/composables/useSwal";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";
import Card from "@/components/Card.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import {getOffices} from "@/Modules/Financial/store/modules/offices/getters";
import Select2 from "@/components/Select2.vue";
import {$formatLocalAmount} from "@/mixins/Helpers";

const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })
const { can } = useHasAbility()

const table = ref(null)

const {locale, datatableLocaleUrl} = useLocales()

const router = useRouter()

const store = useStore()

const query = reactive({
  selectedOffice: ''
})

const offices = computed(() => store.getters['offices/getOffices'])
const selectedCompany = computed(() => store.getters['offices/getSelectedCompany'])

watch(() => offices.value, (value) => {
  if(!query.selectedOffice) {
    query.selectedOffice = value[0]?.id
  }
}, {deep: true})

watch(() => selectedCompany.value, (value) => {
  if(value) {
    store.dispatch("offices/loadSocietyOffices", value?.id)
  }
})

watch(() => query.selectedOffice, (value) => {
  if(value) {
    fetchRecurrentExpenses()
  }
})

watch(() => locale.value, () => {
  if(query.selectedOffice) {
    fetchRecurrentExpenses()
  }
})

const createNewRecurrent = () => {
  router.push({ name: 'settings-recurrence-expense-create', params: { office: query?.selectedOffice } })
}


function fetchRecurrentExpenses(){
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#recurrent-expense-datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'asc']],
    ajax: {
      url: BASE_URL + `/get/${query.selectedOffice}/expense/recurrentDataTable`,
      type: "GET",
      beforeSend: function (request, options) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [{ data: "title" }, {  data: "date" }, {  data: "amount" }, {  data: "status" }, { data: "Actions", responsivePriority: -1 }],
    columnDefs: [
      {
        targets: -5,
        order: "desc",
        render: function (t, a, e, l) {
          return '<span>' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        render: function (t, a, e, l) {
          return '<span>' + t + "</span>";
        },
      },
      {
        targets: -3,
        order: "desc",
        render: function (t, a, e, l) {
          return '<span>' +  $formatLocalAmount(t)  + "</span>";
        },
      },
      {
        targets: -2,
        order: "desc",
        render: function (t, a, e, l) {
          let color = t ? 'success' : 'danger'
          let text = t ? 'recurrentExpense.label.enable' : 'recurrentExpense.label.disable'
          return  `<span class="badge badge-${color} me-3"> ${ i18n.global.t(text) } </span>`;
        },
      },
      {
        width: "15%",
        targets: -1,
        "className": "dt-center",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_EXPENSE_RECURRING)) {
            render += '<a href="javascript:;" data-row="' + l.row + '" data-id="' + e.id + '" class="btn btn-sm btn-clean btn-icon show_btn" ><i data-row="' + l.row + '" data-id="' + e.id + '" class="fas fa-eye fs-2" data-toggle="tooltip" data-placement="top" title="' + i18n.global.t('button.show') + '" style="color: ' + Theme.gray + '"></i></a>'
          }
          if(can(abilities.UPDATE_EXPENSE_RECURRING)) {
            render += '<a href="javascript:;" data-row="' + l.row + '" data-id="' + e.id + '" class="btn btn-sm btn-clean btn-icon edit_btn" ><i data-row="' + l.row + '" data-id="' + e.id + '" class="fas fa-edit fs-2" data-toggle="tooltip" data-placement="top" title="' + i18n.global.t('button.edit') + '" style="color: ' + Theme.gray + '"></i></a>'
          }
          return render
        },
      },
    ],
    language: {
      url:  datatableLocaleUrl.value
    }
  });

  $(document).on("click", "#recurrent-expense-datatable .show_btn", (e) => {
    router.push({ name: 'settings-recurrence-expense-view', params: {id: e.target.getAttribute('data-id')} })
  });

  $(document).on("click", "#recurrent-expense-datatable .edit_btn", (e) => {
    router.push({ name: 'settings-recurrence-expense-edit', params: {id: e.target.getAttribute('data-id')} })
  });

  $(document).on("click", "#recurrent-expense-datatable .delete_btn", (e) => {
    router.push({ name: 'settings-recurrence-expense-edit', params: {id: e.target.getAttribute('data-id')} })
  });


}


onMounted(() => {

})

</script>
